import React from 'react';
import { useRouteMatch } from 'react-router-dom';
import Configs from 'config';
import { get } from 'lodash';
import { DisplayName } from 'components/base/DisplayName';
import MentoringPastClass from 'components/mentoring/MentoringPastClass';
import { ProfileWrapper, EducationWrapper } from 'components/mentoring/styles';
import { MentoringClassWrapper } from 'components/mentoring/styles';
import { getMentee } from 'libs/api/user';
import { setInterestedIntern } from 'libs/api/user';
import { useDataApi } from 'libs/hooks';
import { RouteMatch } from 'modules/types';
import { TMentee, TEducation } from 'modules/user';
import { CWindowWidth } from 'constants/base.const';
import { CUserType, CDegreeType } from 'constants/user.const';
import { EUserType } from 'enums/base.enum';
import { ERoute } from 'enums/route.enum';
import { Avatar, Tabs, Button, Col, Row, Typography, Divider, Spin, Tag, List } from 'antd';
import styled from 'styled-components';
import { MenteeColor } from 'GlobalStyles';
import IconBag from 'assets/icons/bag2.svg';
import IconGraduationCap from 'assets/icons/graduation_cap.svg';

const { TabPane } = Tabs;
const { Text, Title } = Typography;
const MentoringMenteeViewWrapper = styled.div`
  font-size: 16px;
  color: #000;

  .small-btn {
    margin: 10px 5px;
    padding: 0px 12px !important;
  }
  .ant-divider {
    margin: 35px 0px !important;
  }
  .icon {
    margin-bottom: 5px;
    margin-right: 7px;
  }
`;
type TMentoringMenteeViewProps = {
  isInternView?: boolean;
};
const MentoringMenteeView = ({ isInternView = false }: TMentoringMenteeViewProps) => {
  const url = isInternView === false ? ERoute.MentoringMenteeView : ERoute.CompanyInternView;
  const match = useRouteMatch(url);
  const { record_id } = (match?.params as RouteMatch) || {};
  const getPromise = getMentee.bind(null, record_id);
  const [{ data, loading }, setCallback] = useDataApi<TMentee>(getPromise);

  async function handleSetInterested() {
    try {
      await setInterestedIntern({
        mentee_id: record_id,
        is_interested: true,
      });
      setCallback(() => getPromise);
    } catch (e) {
      throw e;
    }
  }

  return (
    <MentoringMenteeViewWrapper>
      <Spin tip='loading...' spinning={loading}>
        <Row
          style={{
            maxWidth: `${CWindowWidth - 300}px`,
            margin: '20px auto',
            padding: '30px 10px',
          }}
          gutter={[32, 16]}
        >
          <Col xs={24} sm={24} md={6} lg={6} xl={6} style={{ textAlign: 'center' }}>
            <ProfileWrapper style={{ maxWidth: '220px' }}>
              <Avatar
                shape='square'
                className='no-hover'
                src={get(data, 'user.profile_image.absolute_url')}
              />
              <Tag className='user-type-tag' color={MenteeColor}>
                {CUserType[EUserType.Mentee]}
              </Tag>
              <div className='name'>{get(data, 'user.fullname')}</div>
            </ProfileWrapper>
            <div style={{ textAlign: 'center', width: '100%', fontSize: '14px' }}>
              <div>
                {DisplayName(get(data, 'user.educations', [])?.slice(-1)[0]?.university_name)}{' '}
                {get(data, 'user.educations', [])?.slice(-1)[0]?.major} 전공
              </div>
            </div>
            {get(data, 'resume_file.absolute_url') && (
              <Button
                style={{ marginTop: '5px' }}
                href={`${Configs.API_HOST}${get(data, 'resume_file.absolute_url')}`}
                className='resume-btn'
              >
                이력서 보기
              </Button>
            )}
          </Col>
          <Col xs={24} sm={24} md={18} lg={18} xl={18}>
            <Title level={4}>
              <img alt='icon' className='icon' src={IconGraduationCap} />
              학력
            </Title>
            <EducationWrapper style={{ marginBottom: '30px' }}>
              <List
                split={false}
                dataSource={get(data, 'user.educations')}
                renderItem={(item: TEducation) => (
                  <List.Item>
                    <List.Item.Meta
                      title={<div className='title'>{DisplayName(get(item, 'university_name'))}</div>}
                      avatar={
                        get(item, 'university_logo_url') ? (
                          <img alt='university_logo' src={get(item, 'university_logo_url')} />
                        ) : (
                          <div className='empty'></div>
                        )
                      }
                      description={
                        <div className='sub-title'>
                          {get(item, 'major')}
                          <span style={{ marginLeft: '20px' }} className='extra'>
                            {CDegreeType[get(item, 'degree_type')]}
                          </span>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </EducationWrapper>
            <Divider />
            <Title level={4}>
              <img alt='icon' className='icon' src={IconBag} />
              경력
            </Title>
            <EducationWrapper style={{ marginBottom: '30px' }}>
              <List
                split={false}
                dataSource={get(data, 'user.work_experiences')}
                renderItem={(item) => (
                  <List.Item>
                    <List.Item.Meta
                      title={<div className='title'>{DisplayName(get(item, 'company_name'))}</div>}
                      avatar={
                        get(item, 'company_logo_url') ? (
                          <img alt='company_logo' src={get(item, 'company_logo_url')} />
                        ) : (
                          <div className='empty'></div>
                        )
                      }
                      description={
                        <div className='sub-title'>
                          {get(item, 'position')}
                          <span style={{ marginLeft: '20px' }} className='extra'>
                            {get(item, 'duration_display')}
                            <span style={{ marginLeft: '20px' }}>{get(item, 'status_display')}</span>
                          </span>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </EducationWrapper>
            {isInternView === false && (
              <>
                <Divider />
                <Title level={4}>{get(data, 'user.fullname')} 멘티와의 지난 수업</Title>
                <MentoringClassWrapper style={{ marginTop: '20px' }}>
                  {!loading && (
                    <MentoringPastClass
                      withId={get(data, 'id')}
                      targetType={EUserType.Mentee}
                      useProfile={false}
                    />
                  )}
                </MentoringClassWrapper>
              </>
            )}
          </Col>
        </Row>
      </Spin>
    </MentoringMenteeViewWrapper>
  );
};

export default MentoringMenteeView;
